/**
 * Accounts payable, in one list.
 *
 * Direct Auto owes money in two shapes and they used to live on different screens:
 *
 *   vehicle  a payment run, the purchase price owed to a seller. Its status is set by
 *            the approval workflow (Winnie approves, TUO releases, the bank processes)
 *            and NOTHING here writes it. Marking a car paid from a list would walk
 *            straight around the two person control, so this service will not do it.
 *
 *   bill     a supplier invoice sitting in expenses. Transport, PPSR, reconditioning.
 *            Nobody countersigns a tow truck invoice, so these can be marked paid here.
 *
 * The planned payment date is writable on both, because deciding when to pay something
 * is not the same as paying it.
 */

import { supabase } from '../lib/supabase'
import { DateRange, ALL_TIME, inRange } from '../lib/dateRange'

export type PayableKind = 'vehicle' | 'bill'
export type PayableStatus = 'awaiting_payment' | 'paid' | 'void'

export interface Payable {
  id: string
  kind: PayableKind
  payee: string
  reference: string
  description: string
  dealId: string | null
  vin: string | null
  stockNumber: string | null
  billDate: string | null
  dueDate: string | null
  plannedDate: string | null
  amount: number
  gst: number
  status: PayableStatus
  paidAt: string | null
  /** Only ever populated for a vehicle payable, from the seller's own record. */
  bsb: string | null
  accountNumber: string | null
  accountName: string | null
  /** True when the approval workflow owns the status and this screen must not touch it. */
  statusLocked: boolean
}

export interface AbaSettings {
  bankCode: string
  userName: string
  userId: string
  traceBsb: string
  traceAccount: string
  remitterName: string
}

export const EMPTY_ABA_SETTINGS: AbaSettings = {
  bankCode: '',
  userName: '',
  userId: '',
  traceBsb: '',
  traceAccount: '',
  remitterName: '',
}

const money = (v: unknown) => Math.round((Number(v) || 0) * 100) / 100
const day = (v: unknown) => (v ? String(v).slice(0, 10) : null)

function vehicleOf(deal: Record<string, unknown> | null): string {
  if (!deal) return 'Vehicle'
  const parts = [deal.autograb_year, deal.autograb_make, deal.autograb_model]
    .map((p) => (p == null ? '' : String(p).trim()))
    .filter(Boolean)
  return parts.length ? parts.join(' ') : 'Vehicle'
}

/**
 * A payment run is paid when the money actually moved, not when it was approved. If the
 * bank has not confirmed it, it is still owed, and showing it as paid would understate
 * what is going out this week.
 */
function vehicleStatus(row: Record<string, unknown>): PayableStatus {
  const status = String(row.status || '').toLowerCase()
  if (status.includes('cancel') || status.includes('void')) return 'void'
  if (row.payment_processed_at) return 'paid'
  if (String(row.payment_status || '').toLowerCase() === 'paid') return 'paid'
  return 'awaiting_payment'
}

// deals is reachable from both of these tables by more than one path, so the embed
// names the foreign key. Without it PostgREST answers 300 and the screen shows nothing.
export async function loadPayables(range: DateRange = ALL_TIME): Promise<Payable[]> {
  const [runsRes, billsRes, linesRes] = await Promise.all([
    supabase
      .from('payment_runs')
      .select(
        'id, deal_id, total_amount, purchase_price, holding_deposit, status, payment_status, ' +
          'payment_processed_at, payment_reference, planned_payment_date, planned_payment_note, ' +
          'released_to_wbc_at, created_at, ' +
          'deal:deals!deal_id(id, autograb_year, autograb_make, autograb_model, autograb_vin, stock_number, ' +
          'seller_name, seller_bsb, seller_account_number, seller_bank_account_name, purchase_reference)',
      )
      .order('created_at', { ascending: true }),
    supabase
      .from('expenses')
      .select(
        'id, deal_id, category, description, amount, gst_amount, supplier, invoice_ref, incurred_on, ' +
          'due_date, planned_payment_date, payment_status, paid_at, payment_reference, created_at, ' +
          'deal:deals!deal_id(id, autograb_year, autograb_make, autograb_model, autograb_vin, stock_number)',
      )
      .order('incurred_on', { ascending: true }),
    /* Winnie, 18 Sep: a vehicle bought from an encumbered private seller pays the financier
       first and the seller the balance, which is two payees against one run. A run with no
       lines is an ordinary single payment and behaves exactly as it always has. */
    supabase.from('payment_run_lines').select('*').order('sequence', { ascending: true }),
  ])

  if (runsRes.error) throw runsRes.error
  if (billsRes.error) throw billsRes.error
  if (linesRes.error) throw linesRes.error

  const linesByRun = new Map<string, Record<string, unknown>[]>()
  for (const raw of (linesRes.data || []) as unknown as Record<string, unknown>[]) {
    const key = String(raw.payment_run_id)
    linesByRun.set(key, [...(linesByRun.get(key) || []), raw])
  }

  const vehicles: Payable[] = ((runsRes.data || []) as unknown as Record<string, unknown>[]).flatMap((row) => {
    const deal = (row.deal || null) as Record<string, unknown> | null
    const lines = linesByRun.get(String(row.id)) || []

    /* A split run becomes one payable per payee, each with its own amount and its own bank
       details, so the ABA file pays both. The run itself is unchanged - the approval gate
       still guards the run, not the line. */
    if (lines.length) {
      return lines.map((l) => ({
        id: String(row.id) + ':' + String(l.id),
        kind: 'vehicle' as PayableKind,
        payee: String(l.payee || 'Payee'),
        reference: String((deal && deal.purchase_reference) || (deal && deal.stock_number) || ''),
        description:
          vehicleOf(deal) +
          (String(l.line_type) === 'payout' ? ' — financier payout' : ' — balance to seller'),
        dealId: deal ? String(deal.id) : null,
        vin: deal && deal.autograb_vin ? String(deal.autograb_vin) : null,
        stockNumber: deal && deal.stock_number ? String(deal.stock_number) : null,
        billDate: day(row.created_at),
        dueDate: null,
        plannedDate: day(row.planned_payment_date),
        amount: money(l.amount),
        gst: 0,
        status: vehicleStatus(row),
        paidAt: row.payment_processed_at ? String(row.payment_processed_at) : null,
        bsb: l.bsb ? String(l.bsb) : null,
        accountNumber: l.account_number ? String(l.account_number) : null,
        accountName: l.account_name ? String(l.account_name) : String(l.payee || ''),
        statusLocked: true,
      }))
    }

    return [{
      id: String(row.id),
      kind: 'vehicle' as PayableKind,
      payee: String((deal && deal.seller_name) || 'Seller'),
      reference: String((deal && deal.purchase_reference) || (deal && deal.stock_number) || ''),
      description: vehicleOf(deal),
      dealId: deal ? String(deal.id) : null,
      vin: deal && deal.autograb_vin ? String(deal.autograb_vin) : null,
      stockNumber: deal && deal.stock_number ? String(deal.stock_number) : null,
      billDate: day(row.created_at),
      dueDate: null,
      plannedDate: day(row.planned_payment_date),
      amount: money(row.total_amount),
      // A vehicle purchase from a private seller carries no GST we can claim, and one
      // from a GST vendor is handled on the purchase record, not here. Showing a made up
      // number in a GST column would be worse than showing nothing.
      gst: 0,
      status: vehicleStatus(row),
      paidAt: row.payment_processed_at ? String(row.payment_processed_at) : null,
      bsb: deal && deal.seller_bsb ? String(deal.seller_bsb) : null,
      accountNumber: deal && deal.seller_account_number ? String(deal.seller_account_number) : null,
      accountName:
        deal && (deal.seller_bank_account_name || deal.seller_name)
          ? String(deal.seller_bank_account_name || deal.seller_name)
          : null,
      statusLocked: true,
    }]
  })

  const bills: Payable[] = ((billsRes.data || []) as unknown as Record<string, unknown>[]).map((row) => {
    const deal = (row.deal || null) as Record<string, unknown> | null
    const status = String(row.payment_status || 'awaiting_payment') as PayableStatus
    return {
      id: String(row.id),
      kind: 'bill' as PayableKind,
      payee: String(row.supplier || 'Supplier'),
      reference: String(row.invoice_ref || ''),
      description: String(row.description || row.category || 'Expense'),
      dealId: deal ? String(deal.id) : row.deal_id ? String(row.deal_id) : null,
      vin: deal && deal.autograb_vin ? String(deal.autograb_vin) : null,
      stockNumber: deal && deal.stock_number ? String(deal.stock_number) : null,
      billDate: day(row.incurred_on) || day(row.created_at),
      dueDate: day(row.due_date),
      plannedDate: day(row.planned_payment_date),
      amount: money(row.amount),
      gst: money(row.gst_amount),
      status: status === 'paid' || status === 'void' ? status : 'awaiting_payment',
      paidAt: row.paid_at ? String(row.paid_at) : null,
      // We do not hold supplier bank details, and inventing them would produce an ABA
      // file that pays the wrong person. Supplier bills export to CSV; only vehicle
      // payments, where the seller's account is on the deal, can go in a bank file.
      bsb: null,
      accountNumber: null,
      accountName: null,
      statusLocked: false,
    }
  })

  const all = [...vehicles, ...bills].filter((p) => inRange(p.billDate, range))

  // Oldest first, which is the order Winnie asked for everywhere else in the app.
  return all.sort((a, b) => String(a.billDate || '').localeCompare(String(b.billDate || '')))
}

export async function setPlannedDate(
  kind: PayableKind,
  id: string,
  date: string | null,
): Promise<void> {
  const table = kind === 'vehicle' ? 'payment_runs' : 'expenses'
  const { error } = await supabase
    .from(table)
    .update({ planned_payment_date: date || null })
    .eq('id', id)
  if (error) throw error
}

/**
 * Bills only. A vehicle payment becomes paid through the approval and release workflow.
 */
export async function markBillPaid(id: string, paidOn: string, reference: string): Promise<void> {
  const { error } = await supabase
    .from('expenses')
    .update({
      payment_status: 'paid',
      paid_at: new Date(paidOn + 'T00:00:00Z').toISOString(),
      payment_reference: reference || null,
    })
    .eq('id', id)
  if (error) throw error
}

export async function markBillUnpaid(id: string): Promise<void> {
  const { error } = await supabase
    .from('expenses')
    .update({ payment_status: 'awaiting_payment', paid_at: null })
    .eq('id', id)
  if (error) throw error
}

const csvCell = (v: unknown) => {
  const s = v == null ? '' : String(v)
  return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s
}

export function payablesCsv(rows: Payable[]): string {
  const head = [
    'Type',
    'Payee',
    'Reference',
    'Description',
    'Stock number',
    'VIN',
    'Date',
    'Due date',
    'Planned date',
    'Amount',
    'GST',
    'Status',
    'Paid at',
  ]
  const body = rows.map((r) =>
    [
      r.kind === 'vehicle' ? 'Vehicle purchase' : 'Supplier bill',
      r.payee,
      r.reference,
      r.description,
      r.stockNumber || '',
      r.vin || '',
      r.billDate || '',
      r.dueDate || '',
      r.plannedDate || '',
      r.amount.toFixed(2),
      r.gst.toFixed(2),
      r.status === 'awaiting_payment' ? 'Awaiting payment' : r.status === 'paid' ? 'Paid' : 'Void',
      r.paidAt ? String(r.paidAt).slice(0, 10) : '',
    ]
      .map(csvCell)
      .join(','),
  )
  return [head.join(','), ...body].join('\r\n') + '\r\n'
}

export async function loadAbaSettings(): Promise<AbaSettings> {
  const { data, error } = await supabase
    .from('admin_settings')
    .select('aba_bank_code, aba_user_name, aba_user_id, aba_trace_bsb, aba_trace_account, aba_remitter_name')
    .limit(1)
    .maybeSingle()

  if (error) throw error
  const row = (data || null) as Record<string, string | null> | null
  if (!row) return { ...EMPTY_ABA_SETTINGS }

  return {
    bankCode: row.aba_bank_code || '',
    userName: row.aba_user_name || '',
    userId: row.aba_user_id || '',
    traceBsb: row.aba_trace_bsb || '',
    traceAccount: row.aba_trace_account || '',
    remitterName: row.aba_remitter_name || '',
  }
}

export async function saveAbaSettings(settings: AbaSettings): Promise<void> {
  const { data, error } = await supabase.from('admin_settings').select('id').limit(1).maybeSingle()
  if (error) throw error
  if (!data) throw new Error('There is no settings row to save these against.')

  const { error: updateError } = await supabase
    .from('admin_settings')
    .update({
      aba_bank_code: settings.bankCode || null,
      aba_user_name: settings.userName || null,
      aba_user_id: settings.userId || null,
      aba_trace_bsb: settings.traceBsb || null,
      aba_trace_account: settings.traceAccount || null,
      aba_remitter_name: settings.remitterName || null,
    })
    .eq('id', (data as { id: number | string }).id)

  if (updateError) throw updateError
}
